import React, { useEffect } from 'react';
import {  useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styles from './PopularServices.module.scss';
import { useAppStateActivitiesWizardSelector, clearSelection } from '../activitiesWizard/activitiesWizardSlice';
import { loadActivities } from '../activitiesWizard/activitiesWizardActionCreators';
import { useAuthSelector } from '../../features/auth/authSlice';
import { Activity } from '../../features/activities/list/activitiesListAppState';
import { Spinner } from '../spinner/Spinner';
import { customFormMap, navigateToActivity } from '../../services/activityService';
import { DropdownOptions } from '../dropdown/Dropdown';
import { firebaseService } from '../../services/firebaseService';

interface PopularServicesProps {
    idPrefix?: string;
}

export const PopularServices = ({ idPrefix }: PopularServicesProps) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { t } = useTranslation();
    const {
        popularItems
    } = useAppStateActivitiesWizardSelector(state => state);
    const { authUser } = useAuthSelector(state => state);
    const { loadingActivities } = useAppStateActivitiesWizardSelector(state => state);

    useEffect(() => {
        dispatch(clearSelection(null));
        dispatch(loadActivities(authUser?.Id, 'all'));
    }, [dispatch, authUser]);

    let handleOnStart = (selectedItem: Activity) => {
        firebaseService
            .app()
            .analytics()
            .logEvent('popular_services_click', {
                service: selectedItem.Item,
                platform: 'web'
            });
        if (!authUser && selectedItem.Id.toUpperCase() === customFormMap.WATER_BILL.toUpperCase()) {
            history.push(`/login?redirectto=payment&guest_enabled=true`);
            return;
        }

        if(!authUser && selectedItem.Id.toUpperCase() === customFormMap.QUICK_PERMIT.toUpperCase()) {
            history.push(`/login`);
            return;
        }

        if (!authUser?.Email && !selectedItem.GuestEnabled) {
            history.push(`/login?activityId=${selectedItem.Id}`);
            return;
        }

        const { historyPushLocation, dispatchFunction } = navigateToActivity(selectedItem as Activity);

        if (dispatchFunction) dispatch(dispatchFunction);
        if (historyPushLocation) history.push(historyPushLocation);
    }

    const displayItemText = (item: DropdownOptions) => {
        // todo: add translations
        if (item.item?.Action === 'APPLY_FOR') {
            return t('Activity.ApplyForA') + ' ' + t(item.translation!);
        }
        if (item.key === 'WATER_BILL') {
            return t('Activity.PayYourWaterBill');
        }
        if (item.key === 'OPEN_PERMITS') {
            return t('Activity.FindOpenPermits');
        }
        if (item.key === 'GARBAGE_RECYCLING_MISSED_COLLECTION') {
            return t('Activity.ReportMissedGarbageCollection');
        }
        if (item.key === 'CITY_ISSUED_TICKET') {
            return t('Activity.PayCityIssuedCitation');
        }

        return t(item.translation!);
    }

    return (
        <>
        {
            loadingActivities ?
            <div className={`${styles.topInnerPanelContainer} ${styles.spinnerContainer}`}>
                <Spinner isLoading={loadingActivities} />
            </div>
            :
            <section className={styles.topInnerPanelContainer} aria-labelledby={idPrefix ? `${idPrefix}-popular-services-heading` : 'popular-services-heading'}>
                <h1 id={idPrefix ? `${idPrefix}-popular-services-heading` : 'popular-services-heading'} className={styles.title}>{t('Activity.PopularServices')}</h1>
                <div className={styles.gridContainer} role="group" aria-label={t('Activity.PopularServices')}>
                    {popularItems[0]?.translation && popularItems[0]?.item &&
                        popularItems.map((item, i) => {
                            const itemText = displayItemText(item);
                            return (
                                <button 
                                    type="button"
                                    className={styles.gridItem} 
                                    key={i} 
                                    onClick={() => handleOnStart(item.item!)}
                                    aria-label={itemText}
                                >
                                    <span 
                                        className={styles.gridItemServiceIcon} 
                                        style={item.item?.PopularServiceIconURL ? { background: `url('${item.item?.PopularServiceIconURL}') no-repeat center`, backgroundSize: 'contain', width: '32px', height: '32px' } : {}}
                                        aria-hidden="true"
                                    ></span>
                                    <span className={styles.gridItemText}>{itemText}</span>
                                    <span className={styles.gridItemCarrotIcon} aria-hidden="true"></span>
                                </button>
                            );
                        })
                    }
                </div>
                <div className={styles.viewAllLinkContainer}>
                    <button 
                        type="button"
                        className={styles.viewAllLink} 
                        onClick={() => history.push('/view-all-services')}
                    >
                        {t('Activity.ViewAllServices')}
                    </button>
                </div>
            </section>
        }
        </>
    );
}
